import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";

import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Coins,
  Database,
  Layers3,
  ShieldCheck,
  Tag,
  Wallet,
} from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { Input } from "@/components/ui/input";

import { Switch } from "@/components/ui/switch";

import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import type { BlockchainNetwork } from "@/interfaces/blockchainNetwork";
import FeatureToggleCard from "./FeatureToggleCard";

const schema = z.object({
  name: z.string().min(2),

  code: z.string().min(2),

  coinSymbol: z.string().min(2),

  chainId: z.string().nullable(),

  feeAssetSymbol: z.string(),

  explorerUrl: z.string().nullable(),

  rpcUrl: z.string().nullable(),

  wsUrl: z.string().nullable(),

  decimals: z.number(),

  confirmations: z.number(),

  priority: z.number(),

  networkType: z.enum(["MAINNET", "TESTNET"]),

  enabled: z.boolean(),

  depositEnabled: z.boolean(),

  withdrawEnabled: z.boolean(),

  isEvm: z.boolean(),

  isUtxo: z.boolean(),

  supportsTokens: z.boolean(),

  supportsMemo: z.boolean(),

  supportsTag: z.boolean(),
});

export type BlockchainNetworkFormValues = z.infer<typeof schema>;

interface Props {
  loading?: boolean;

  initialValues?: Partial<BlockchainNetwork>;

  onSubmit: (values: BlockchainNetworkFormValues) => void;
}

const BlockchainNetworkForm = ({ loading, initialValues, onSubmit }: Props) => {
  const form = useForm<BlockchainNetworkFormValues>({
    resolver: zodResolver(schema),

    defaultValues: {
      name: "",

      code: "",

      coinSymbol: "",

      chainId: null,

      feeAssetSymbol: "",

      explorerUrl: null,

      rpcUrl: null,

      wsUrl: null,

      decimals: 18,

      confirmations: 12,

      priority: 1,

      networkType: "MAINNET",

      enabled: true,

      depositEnabled: true,

      withdrawEnabled: true,

      isEvm: true,

      isUtxo: false,

      supportsTokens: true,

      supportsMemo: false,

      supportsTag: false,
    },
  });

  useEffect(() => {
    if (!initialValues) return;

    form.reset({
      ...form.getValues(),
      ...initialValues,
    });
  }, [initialValues]);

  return (
    <Form {...form}>
      <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <h3 className="mb-6 text-lg font-semibold">General Information</h3>

          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coinSymbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Coin Symbol</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="feeAssetSymbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fee Asset</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div>
          <h3 className="mb-6 text-lg font-semibold">
            Blockchain Configuration
          </h3>

          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="chainId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chain ID</FormLabel>

                  <FormControl>
                    <Input
                      placeholder="137"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="decimals"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Decimals</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Required Confirmations</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="networkType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Network Type</FormLabel>

                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      <SelectItem value="MAINNET">Mainnet</SelectItem>

                      <SelectItem value="TESTNET">Testnet</SelectItem>
                    </SelectContent>
                  </Select>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-6 text-lg font-semibold">Infrastructure</h3>

          <div className="space-y-6">
            <FormField
              control={form.control}
              name="explorerUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Explorer URL</FormLabel>

                  <FormControl>
                    <Input
                      placeholder="https://etherscan.io"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rpcUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RPC URL</FormLabel>

                  <FormControl>
                    <Input
                      placeholder="https://..."
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="wsUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WebSocket URL</FormLabel>

                  <FormControl>
                    <Input
                      placeholder="wss://..."
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-6 text-lg font-semibold">Network Features</h3>

          <div className="grid gap-5 lg:grid-cols-2">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Network Enabled"
                  description="Enable this blockchain throughout the platform."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<ShieldCheck className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="depositEnabled"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Deposits"
                  description="Allow users to deposit assets."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<ArrowDownToLine className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="withdrawEnabled"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Withdrawals"
                  description="Allow withdrawals on this blockchain."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<ArrowUpFromLine className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="isEvm"
              render={({ field }) => (
                <FeatureToggleCard
                  title="EVM Compatible"
                  description="Ethereum Virtual Machine support."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<Layers3 className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="isUtxo"
              render={({ field }) => (
                <FeatureToggleCard
                  title="UTXO Model"
                  description="Bitcoin-style UTXO accounting."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<Database className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="supportsTokens"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Supports Tokens"
                  description="Native support for token contracts."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<Coins className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="supportsMemo"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Memo Required"
                  description="Transactions may require a memo."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<Wallet className="h-5 w-5 text-primary" />}
                />
              )}
            />

            <FormField
              control={form.control}
              name="supportsTag"
              render={({ field }) => (
                <FeatureToggleCard
                  title="Destination Tag"
                  description="Transactions require destination tag."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  icon={<Tag className="h-5 w-5 text-primary" />}
                />
              )}
            />
          </div>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-background pt-6">
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Blockchain Network"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BlockchainNetworkForm;
